/* @flow */

import React from 'react';

import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import ButtonComponent from 'components/buttons/button';
import IntlMessageComponent from 'components/formatters/intlMessage';
import TooltipComponent from 'components/toolTips/index';

import {
  STEP_SELECT,
  STEP_QR,
  STEP_SMS_MOBILE,
  STEP_SMS_CODE,
  STEP_DONE
} from './index';

type TwoFactorSetupViewComponentProps = {
  step: string;
  isProcessing: boolean;
  smsSelected: boolean;
  qrSelected: boolean;
  qrCodeUrl: ?string;
  qrPin: string;
  wrongQrPin: boolean;
  mobile: string;
  smsCode: string;
  wrongSmsCode: boolean;
  smsError: ?string;
  cancelLabelId: ?string;
  onCancel: ?() => void;
  handleSmsSelectedChange: (value: boolean) => void;
  handleQrSelectedChange: (value: boolean) => void;
  handleQrPinChange: (value: string) => void;
  handleMobileChange: (value: string) => void;
  handleSmsCodeChange: (value: string) => void;
  goBack: () => void;
  validateSelection: () => void;
  validateQr: () => void;
  sendSmsCode: () => void;
  validateSmsCode: () => void;
}

export default function TwoFactorSetupViewComponent(props: TwoFactorSetupViewComponentProps) {

  const renderCancel = () => {
    if (!props.onCancel) return null;
    return (
      <ButtonComponent
        color="default"
        variant="contained"
        onClick={props.onCancel}
      ><IntlMessageComponent id={props.cancelLabelId ?? 'general.cancel'} /></ButtonComponent>
    );
  };

  const renderBack = () => (
    <Box component="span" mr={1}>
      <ButtonComponent
        color="default"
        variant="contained"
        onClick={props.goBack}
      ><IntlMessageComponent id="general.cancel" /></ButtonComponent>
    </Box>
  );

  const renderSelect = () => (
    <React.Fragment>
      <Box mb={3}><IntlMessageComponent id="twofactor.select.intro" /></Box>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={props.smsSelected} onChange={(e) => props.handleSmsSelectedChange(e.target.checked)} />}
          label={<IntlMessageComponent id="twofactor.select.sms" />}
        />
      </Box>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={props.qrSelected} onChange={(e) => props.handleQrSelectedChange(e.target.checked)} />}
          label={<IntlMessageComponent id="twofactor.select.qr" />}
        />
      </Box>
      <Box mt={3}>
        {/* {renderCancel()} */}
        <Box component="span" ml={props.onCancel ? 1 : 0}>
          <ButtonComponent
            color="primary"
            variant="contained"
            isProcessing={props.isProcessing}
            disabled={!props.smsSelected && !props.qrSelected}
            onClick={props.validateSelection}
          ><IntlMessageComponent id="general.validate" /></ButtonComponent>
        </Box>
      </Box>
    </React.Fragment>
  );

  const renderQr = () => (
    <React.Fragment>
      <Box mb={3}><IntlMessageComponent id="twofactor.qr.intro" /></Box>
      {props.qrCodeUrl && <Box><img src={props.qrCodeUrl} alt="QR Code" /></Box>}
      <Box display="flex" alignItems="center" pageName="TwoFactor.Setup" elementName="QRCode">
        <TextField
          id="twofactor.qrpin"
          label={<IntlMessageComponent id="twofactor.qr.codelabel" />}
          value={props.qrPin}
          onChange={(e) => props.handleQrPinChange(e.target.value)}
          margin="normal"
          required
        />
        <Box ml={1}>
          <TooltipComponent pageName="TwoFactor.Setup" elementName="QRCode" />
        </Box>
      </Box>
      <Box mt={2}>
        {renderBack()}
        <ButtonComponent
          color="primary"
          variant="contained"
          isProcessing={props.isProcessing}
          onClick={props.validateQr}
        ><IntlMessageComponent id="general.validate" /></ButtonComponent>
      </Box>
      {props.wrongQrPin &&
        <Box mt={2} color="error.main"><IntlMessageComponent id="twofactor.qr.invalid" /></Box>}
    </React.Fragment>
  );

  const renderSmsMobile = () => (
    <React.Fragment>
      <Box><IntlMessageComponent id="twofactor.sms.mobileintro" /></Box>
      <Box mt={1} mb={3}>
        <Typography variant="h5" color="primary">{props.mobile}</Typography>
      </Box>
      <Box color="error.main"><strong><IntlMessageComponent id="twofactor.sms.mobilequestion" /></strong></Box>
      <Box mb={2}><IntlMessageComponent id="twofactor.sms.mobilehint" /></Box>
      <Box>
        <TextField
          id="twofactor.mobile"
          label={<IntlMessageComponent id="twofactor.sms.mobilelabel" />}
          helperText={<IntlMessageComponent id="twofactor.sms.mobilehelper" />}
          value={props.mobile}
          onChange={(e) => props.handleMobileChange(e.target.value)}
          margin="normal"
          fullWidth
          required
        />
      </Box>
      <Box mt={2}>
        {renderBack()}
        <ButtonComponent
          color="primary"
          variant="contained"
          isProcessing={props.isProcessing}
          onClick={props.sendSmsCode}
        ><IntlMessageComponent id="twofactor.sms.sendbutton" /></ButtonComponent>
      </Box>
      {props.smsError &&
        <Box mt={2} color="error.main"><IntlMessageComponent id={props.smsError} /></Box>}
    </React.Fragment>
  );

  const renderSmsCode = () => (
    <React.Fragment>
      <Box mb={2}><IntlMessageComponent id="twofactor.sms.codeintro" /></Box>
      <Box>
        <TextField
          id="twofactor.smscode"
          label={<IntlMessageComponent id="twofactor.sms.codelabel" />}
          value={props.smsCode}
          onChange={(e) => props.handleSmsCodeChange(e.target.value)}
          margin="normal"
          required
        />
      </Box>
      <Box mt={2}>
        {renderBack()}
        <ButtonComponent
          color="primary"
          variant="contained"
          isProcessing={props.isProcessing}
          onClick={props.validateSmsCode}
        ><IntlMessageComponent id="general.validate" /></ButtonComponent>
      </Box>
      {props.wrongSmsCode &&
        <Box mt={2} color="error.main"><IntlMessageComponent id="twofactor.sms.invalid" /></Box>}
    </React.Fragment>
  );

  const renderDone = () => (
    <Box><IntlMessageComponent id="twofactor.done.text" /></Box>
  );

  const renderStep = () => {
    switch (props.step) {
      case STEP_SELECT: return renderSelect();
      case STEP_QR: return renderQr();
      case STEP_SMS_MOBILE: return renderSmsMobile();
      case STEP_SMS_CODE: return renderSmsCode();
      case STEP_DONE: return renderDone();
      default: return null;
    }
  };

  return (
    <React.Fragment>
      {renderStep()}
      <Box mt={5}>
        <strong><IntlMessageComponent id="twofactor.help" /></strong>
        <Box><IntlMessageComponent id="twofactor.help.text" /></Box>
      </Box>
    </React.Fragment>
  );
}
